import { createIsomorphicFn } from '@tanstack/react-start';
import {
  deleteCookie as serverDeleteCookie,
  getCookie as serverGetCookie,
  setCookie as serverSetCookie,
} from '@tanstack/react-start/server';
import Cookies from 'js-cookie';
import { LANGUAGE_KEY } from 'consts/keys';

type CookieAttributes = Cookies.CookieAttributes;

type NormalizedCookieOptions = Omit<
  CookieAttributes,
  'expires' | 'sameSite'
> & {
  expires?: Date;
  sameSite?: 'strict' | 'lax' | 'none';
};

const normalizeServerOptions = (
  options?: CookieAttributes
): NormalizedCookieOptions | undefined => {
  if (!options) return undefined;

  const { expires, sameSite, ...rest } = options;
  const normalized: NormalizedCookieOptions = { ...rest };

  if (typeof expires === 'number') {
    normalized.expires = new Date(Date.now() + expires * 24 * 60 * 60 * 1000);
  } else if (expires instanceof Date) {
    normalized.expires = expires;
  }

  if (typeof sameSite === 'boolean') {
    normalized.sameSite = (sameSite as boolean) ? 'strict' : 'lax';
  } else if (typeof sameSite === 'string') {
    normalized.sameSite = sameSite.toLowerCase() as 'strict' | 'lax' | 'none';
  }

  return normalized;
};

export const getCookie = createIsomorphicFn()
  .server((name: string): string | undefined => {
    return serverGetCookie(name) ?? undefined;
  })
  .client((name: string): string | undefined => {
    return Cookies.get(name);
  });

export const setCookie = createIsomorphicFn()
  .server((name: string, value: string, options?: CookieAttributes) => {
    serverSetCookie(name, value, normalizeServerOptions(options));
  })
  .client((name: string, value: string, options?: CookieAttributes) => {
    Cookies.set(name, value, options);
  });

export const deleteCookie = createIsomorphicFn()
  .server((name: string) => {
    serverDeleteCookie(name);
  })
  .client((name: string) => {
    Cookies.remove(name);
  });

export const getLocaleCookie = createIsomorphicFn()
  .server((): string | undefined => {
    return serverGetCookie(LANGUAGE_KEY) ?? undefined;
  })
  .client((): string | undefined => {
    return Cookies.get(LANGUAGE_KEY);
  });
